import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Globe,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  Mail,
  UploadIcon,
} from 'lucide-react';
import { z } from 'zod';

import { Button, CustomSelect, RichTextEditor, SaveDraftModal, Toggle } from '@/components/ui';
import FileUpload from '@/components/ui/FileUpload';
import Input from '@/components/ui/Input/Input';
import { Heading, Paragraph } from '@/components/ui/Typography';
import { useAllCountries } from '@/lib/api/hooks/region';
import { useCreateMember, useMember, useMemberCategories, useUpdateMember } from '@/lib/api/hooks/member';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { MemberFormSchema, type Country, type MemberCategory } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { Loader } from '@/components/ui/Loader';

type MemberFormInput = z.input<typeof MemberFormSchema>;
type MemberFormOutput = z.output<typeof MemberFormSchema>;
type SocialType = 'WEBSITE' | 'LINKEDIN' | 'X_TWITTER' | 'FACEBOOK' | 'INSTAGRAM' | 'YOUTUBE' | 'EMAIL';

const DEFAULT_FORM_VALUES: MemberFormInput = {
  fullName: '',
  countryId: '',
  memberCategory: 'MEMBER',
  isBio: false,
  bio: '',
  socialMediaLinks: [],
  imageUrl: '',
};

const SOCIAL_TYPE_OPTIONS: Array<{ value: SocialType; label: string }> = [
  { value: 'WEBSITE', label: 'Website' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'X_TWITTER', label: 'X / Twitter' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'EMAIL', label: 'Email' },
];

const getSocialPlaceholder = (type: SocialType): string => {
  switch (type) {
    case 'LINKEDIN':
      return 'linkedin.com/in/username';
    case 'X_TWITTER':
      return 'x.com/username';
    case 'FACEBOOK':
      return 'facebook.com/username';
    case 'INSTAGRAM':
      return 'instagram.com/username';
    case 'YOUTUBE':
      return 'youtube.com/@username';
    case 'EMAIL':
      return 'name@domain.com';
    case 'WEBSITE':
    default:
      return 'website.com';
  }
};

const getSocialIcon = (type: SocialType): React.ReactNode => {
  switch (type) {
    case 'LINKEDIN':
      return <Linkedin className="h-4 w-4 text-[#0077B5]" />;
    case 'X_TWITTER':
      return <Twitter className="h-4 w-4 text-black" />;
    case 'FACEBOOK':
      return <Facebook className="h-4 w-4 text-[#1877F2]" />;
    case 'INSTAGRAM':
      return <Instagram className="h-4 w-4 text-[#E4405F]" />;
    case 'YOUTUBE':
      return <Youtube className="h-4 w-4 text-[#FF0000]" />;
    case 'EMAIL':
      return <Mail className="h-4 w-4 text-[#6B7280]" />;
    case 'WEBSITE':
    default:
      return <Globe className="h-4 w-4 text-[#6B7280]" />;
  }
};

const detectSocialType = (value: string): SocialType => {
  const lower = value.toLowerCase();
  if (lower.startsWith('mailto:') || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'EMAIL';
  if (lower.includes('linkedin.com')) return 'LINKEDIN';
  if (lower.includes('x.com') || lower.includes('twitter.com')) return 'X_TWITTER';
  if (lower.includes('facebook.com') || lower.includes('fb.com')) return 'FACEBOOK';
  if (lower.includes('instagram.com')) return 'INSTAGRAM';
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) return 'YOUTUBE';
  return 'WEBSITE';
};

const cleanSocialValue = (raw: string, type: SocialType): string => {
  if (type === 'EMAIL') {
    return raw.replace(/^mailto:/i, '').trim();
  }
  return raw.replace(/^https?:\/\//i, '').trim();
};

const normalizeSocialForPayload = (type: SocialType, value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (type === 'EMAIL') {
    const clean = trimmed.replace(/^mailto:/i, '').trim();
    return clean ? `mailto:${clean}` : '';
  }

  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const mapMemberToFormValues = (member?: Record<string, unknown>): MemberFormInput => {
  if (!member) {
    return DEFAULT_FORM_VALUES;
  }

  const socialMediaLinks = Array.isArray(member.socialMediaLinks) ? (member.socialMediaLinks as string[]) : [];
  const mappedSocial = socialMediaLinks.length
    ? socialMediaLinks.map((link) => {
        const type = detectSocialType(String(link || ''));
        return { type, value: cleanSocialValue(String(link || ''), type) };
      })
    : [];

  return {
    ...DEFAULT_FORM_VALUES,
    fullName: String(member.fullName || ''),
    countryId: String(member.countryId || ''),
    memberCategory: String(member.memberCategory || 'MEMBER') as MemberFormInput['memberCategory'],
    isBio: member.isBio !== undefined ? Boolean(member.isBio) : true,
    bio: String(member.bio || ''),
    socialMediaLinks: mappedSocial,
    imageUrl: String(member.imageUrl || ''),
  };
};

const SectionHeader = ({ index, title }: { index: number; title: string }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#681628] text-xs font-semibold text-white">
      {index}
    </div>
    <Heading level={4} className="text-lg font-semibold text-gray-900">
      {title}
    </Heading>
  </div>
);

const MemberEditor: React.FC = () => {
  const { memberId } = useParams<{ memberId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(memberId);
  const existingMember = location.state?.member as Record<string, unknown> | undefined;

  const { data: memberData, isLoading: loadingMember } = useMember(memberId ?? "");
  const { data: countriesData } = useAllCountries();
  const { data: memberCategoriesData } = useMemberCategories();

  const { mutateAsync: createMember, isPending: isCreating } = useCreateMember();
  const { mutateAsync: updateMember, isPending: isUpdating } = useUpdateMember();
  const fileUploadMutation = useFileUpload();

  const fileUploadRef = useRef<HTMLDivElement>(null);
    const submitIntentRef = useRef<'DRAFT' | 'PUBLISH'>('DRAFT');

  const [isDraftSaving, setIsDraftSaving] = useState(false);


  const [localImageFile, setLocalImageFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<MemberFormInput, unknown, MemberFormOutput>({
    resolver: zodResolver(MemberFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'all',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'socialMediaLinks',
  });

  const fullName = watch('fullName');
  const isBio = watch('isBio');
  const socialRows = watch('socialMediaLinks');

  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isUploadingImage;

  useEffect(() => {
    const member = (memberData?.data as Record<string, unknown> | undefined) || existingMember;

    if (isEditMode && member) {
      const nextValues = mapMemberToFormValues(member);
      reset(nextValues);
      setExistingImageUrl(nextValues.imageUrl);
      setLocalImageFile(null);
      setLocalPreviewUrl('');
      return;
    }

    if (!isEditMode) {
      reset(DEFAULT_FORM_VALUES);
      setExistingImageUrl('');
      setLocalImageFile(null);
      setLocalPreviewUrl('');
    }
  }, [isEditMode, memberData, existingMember, reset]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  useEffect(() => {
    if (!isBio) {
      setValue('bio', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [isBio, setValue]);

  const countries = useMemo(() => {
    const allCountries = (countriesData?.data || []) as Country[];

    return allCountries
      .slice()
      .sort((a, b) => a.countryName.localeCompare(b.countryName))
      .map((country) => ({
        value: country.countryId,
        label: country.countryName,
        flagUrl: country.flagUrl,
      }));
  }, [countriesData]);

  const categoryOptions = useMemo(() => {
    const categories = (memberCategoriesData?.data || []) as MemberCategory[];
    return categories.map((category) => ({ value: category.value, label: category.displayName }));
  }, [memberCategoriesData]);

  const displayImageUrl = localPreviewUrl || existingImageUrl;

  const handleRemoveImage = useCallback(() => {
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalImageFile(null);
    setExistingImageUrl('');
    setValue('imageUrl', '', { shouldDirty: true, shouldValidate: true });
  }, [setValue]);

  const handleImageFileChange = useCallback(
    (files: File[] | null) => {
      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];
      const objectUrl = URL.createObjectURL(file);

      setLocalPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });

      setLocalImageFile(file);
      setValue('imageUrl', objectUrl, { shouldDirty: true, shouldValidate: true });
    },
    [setValue]
  );

  const handleBackClick = useCallback(() => {
    if (isDirty || localImageFile) {
       setShowUnsavedModal(true);
      return;
    }

    navigate('/admin/members');
  }, [isDirty, localImageFile, navigate]);

  const submitWithStatus = async (data: MemberFormOutput, status: 'DRAFT' | 'PUBLISH') => {
       if(status === 'DRAFT') {
      setIsDraftSaving(true);
    }

    try {
      let finalImageUrl = existingImageUrl || data.imageUrl;

      if (localImageFile) {
        setIsUploadingImage(true);
        const uploadResult = await fileUploadMutation.mutateAsync({ file: localImageFile, category: 'IMAGE' });
        finalImageUrl = uploadResult?.data?.fileUrl || '';
        setIsUploadingImage(false);
      }

      const socialMediaLinks = data.socialMediaLinks
        .map((item) => normalizeSocialForPayload(item.type, item.value))
        .filter(Boolean);

      const payload = {
        fullName: data.fullName.trim(),
        countryId: data.countryId,
        memberCategory: data.memberCategory,
        isBio: data.isBio,
        bio: data.isBio ? data.bio : '',
        socialMediaLinks,
        imageUrl: finalImageUrl,
        status,
      };

      if (isEditMode && memberId) {
        await updateMember({ memberId, data: payload });
      } else {
        await createMember(payload);
      }

      showToast.success(status === 'PUBLISH' ? 'Member published successfully' : 'Member saved as draft');
      navigate('/admin/members');
    } catch (error) {
      console.error(error);
      showToast.error('Failed to save member. Please try again.');
      setIsUploadingImage(false);
    } finally {
      if(status === 'DRAFT') {
        setIsDraftSaving(false);
      }
    }
  };

  if (loadingMember && isEditMode) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <Loader size="md" variant="wine" message="Loading member..." />
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6 relative">
      <div className="flex items-center justify-between sticky top-0 z-10 bg-white">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBackClick}
          className="!px-0 !py-0 text-[#047857] font-semibold hover:underline flex items-center gap-2"
          disabled={isBusy}
        >
          <ArrowLeft height={16} width={24} /> Back to Leadership
        </Button>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            loading={isSaving}
            disabled={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'DRAFT'))}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            loading={isSaving || isUploadingImage}
            disabled={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'PUBLISH'))}
          >
            Publish
          </Button>
        </div>
      </div>

      <div className="mx-auto max-w-6xl">
        <div className="border-b border-gray-200 px-4 py-4 md:px-7">
          <Heading level={3} className="mb-3 leading-[1.1] font-semibold text-[#0F172A]">
            {isEditMode ? 'Edit Member' : 'Create Member'}
          </Heading>
          <nav className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => navigate('/admin/members')}
              className="text-wine-100 hover:text-wine-200 hover:underline"
              disabled={isBusy}
            >
              Leadership
            </button>
            {isEditMode ? (
              <>
                <span className="text-gray-400">/</span>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/members/${memberId}`)}
                  className="text-wine-100 hover:text-wine-200 hover:underline"
                  disabled={isBusy || !memberId}
                >
                  {(fullName || '').trim() || 'Member'}
                </button>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">Edit</span>
              </>
            ) : (
              <>
                <span className="text-gray-400">/</span>
                <span className="text-gray-600">Create</span>
              </>
            )}
          </nav>
        </div>

        <form className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2" onSubmit={(event) => event.preventDefault()}>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={1} title="Basic Information" />

            <div className="grid grid-cols-1 items-end gap-6 gap-y-8 md:grid-cols-2">
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Full Name"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.fullName?.message ? String(errors.fullName.message) : undefined}
                    $marginbottom="0"
                    placeholder="Enter full name"
                    className="col-span-2 "
                  />
                )}
              />

              <Controller
                name="memberCategory"
                control={control}
                render={({ field }) => (
                  <div>
                    <CustomSelect
                      value={field.value ?? 'MEMBER'}
                      label="Role / Position"
                      onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? 'MEMBER' : value)}
                      options={categoryOptions}
                      placeholder="Select role or position"
                      disabled={isBusy}
                      error={errors.memberCategory?.message ? String(errors.memberCategory.message) : undefined}
                      required
                    />
                  </div>
                )}
              />

              <Controller
                name="countryId"
                control={control}
                render={({ field }) => (
                  <div>
              
                    <CustomSelect
                      label="Country"
                      value={field.value ?? ''}
                      onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                      options={countries}
                      placeholder="Select country"
                      disabled={isBusy}
                      error={errors.countryId?.message ? String(errors.countryId.message) : undefined}
                      required
                    />
      
                  </div>
                )}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={2} title="Profile Photo" />

            <div className="grid grid-cols-1 items-center border border-gray-300 p-4 rounded-lg gap-4 xxl:grid-cols-[1fr_210px] lg:grid-cols-[1fr_180px] bg-[f3f4f66b]">
              <div className="flex flex-col items-center justify-center gap-4 h-full">
                <Controller
                  name="imageUrl"
                  control={control}
                  render={() => (
                    <FileUpload
                      ref={fileUploadRef}
                      label=""
                      placeholder=""
                      showPreview={false}
                      $maxSizeMB={10}
                      multiple={false}
                      allowedFileTypes={['image/*']}
                      onChange={handleImageFileChange}
                      error={errors.imageUrl?.message ? String(errors.imageUrl.message) : undefined}
                      containerClassName="!border-0 !p-0 min-h-[220px]"
                      disabled={isBusy}
                    >
                      <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-2 text-center ">
                        <UploadIcon className="h-8 w-8 text-green-700" />
                        <Paragraph className="!text-sm">Drag & drop image here</Paragraph>
                        <Paragraph className="!text-sm">or browse to upload</Paragraph>
                        <Paragraph className="!text-sm">Supported: PNG, JPG, JPEG (max 10MB)</Paragraph>
                        <Button type="button" variant="secondary"  disabled={isBusy}>
                          Browse File
                        </Button>
                      </div>
                    </FileUpload>
                  )}
                />

             
              </div>

              <div className="flex flex-col items-center justify-center gap-2 h-fit relative">
                <div className="w-full xxl:h-[210px] lg:h-[180px] overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                  {displayImageUrl ? (
                    <img src={displayImageUrl} alt="Profile" className="h-full w-full object-cover" />
                  ) : null}
                </div>
                  {displayImageUrl ? (
                    <div onClick={handleRemoveImage} className="flex h-8 w-8 cursor-pointer absolute bottom-2 right-2 items-center justify-center rounded-md bg-white border border-gray-300 hover:border-red-500 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" /> 
                    </div>
                ) : null}
              </div>
            </div>
          </section>

          <div className="col-span-2">

        

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={3} title="Biography" />

            <Controller
              name="isBio"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Add Bio"
                  disabled={isBusy}
                  className="justify-start gap-4"
                />
              )}
            />

            {isBio ? (
              <div className="mt-4">
                <Controller
                  name="bio"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Write the leadership biography here..."
                      disabled={isBusy}
                      showExtendedToolbar={true}
                      error={errors.bio?.message ? String(errors.bio.message) : undefined}
                    />
                  )}
                />
              </div>
            ) : null}
          </section>

        
            </div>
              <section className="rounded-xl border border-gray-200 bg-white p-5">
            <SectionHeader index={4} title="Social & Contact Links" />

            <div className="space-y-6">
              {fields.map((field, index) => {
                const rowType = (socialRows?.[index]?.type || 'WEBSITE') as SocialType;
                const rowError = errors.socialMediaLinks?.[index]?.value?.message;

                return (
                  <div key={field.id} className="">


                    <div className="grid grid-cols-[40px_1fr_auto_40px] items-end gap-2">
                      <div className="flex h-[2.75rem] w-full items-center justify-center rounded-md border border-gray-200 bg-white">
                      {getSocialIcon(rowType)}
                      </div>

                      <Controller
                        name={`socialMediaLinks.${index}.value`}
                        control={control}
                        render={({ field: valueField }) => (
                          <Input
                            value={valueField.value}
                            onChange={valueField.onChange}
                            onBlur={valueField.onBlur}
                            error={rowError ? String(rowError) : undefined}
                            $marginbottom="0"
                            placeholder={getSocialPlaceholder(rowType)}
                          />
                        )}
                      />

                      <Controller
                        name={`socialMediaLinks.${index}.type`}
                        control={control}
                        render={({ field: typeField }) => (
                          <CustomSelect
                            value={String(typeField.value ?? 'WEBSITE')}
                            onChange={(value) => typeField.onChange(Array.isArray(value) ? value[0] ?? 'WEBSITE' : value)}
                            onBlur={typeField.onBlur}
                            options={SOCIAL_TYPE_OPTIONS}
                            disabled={isBusy}
                          />
                        )}
                      />

                      <div
                        className=" rounded-md border border-gray-200 h-[2.75rem] flex items-center justify-center hover:border-red-200 cursor-pointer hover:bg-red-50"
                        onClick={() => {
                          remove(index);
                        }}
                        aria-label="Remove link"
                      >
                        <Trash2 className="h-6 w-6 text-red-300" />
                      </div>
                    </div>
                  </div>
                );
              })}

              <Button
                type="button"
                variant="secondary"
                onClick={() => append({ type: 'WEBSITE', value: '' })}
                disabled={isBusy}
                className="mt-2 inline-flex items-center gap-2 border-[#0F766E] text-[#0F766E] hover:bg-[#F0FDFA]"
              >
                <Plus className="h-4 w-4" /> Add Another Link
              </Button>

              {typeof errors.socialMediaLinks?.message === 'string' ? (
                <Paragraph className="mt-1 text-xs text-red-600">{errors.socialMediaLinks.message}</Paragraph>
              ) : null}
            </div>
          </section>
        </form>
      </div>

      <SaveDraftModal
              isOpen={showUnsavedModal}
              onClose={() => setShowUnsavedModal(false)}
              onConfirm={() => {
                setShowUnsavedModal(false);
                submitIntentRef.current = 'DRAFT';
                void handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
              }}
              onDiscard={() => {
                setShowUnsavedModal(false);
                navigate('/admin/history');
              }}
              title='Save History?'
              subtitle='You have unsaved changes. Save as draft before leaving?'
              confirmButtonText='Save as Draft'
              discardButtonText='Discard'
              isLoading={isDraftSaving}
            />
    </div>
  );
};

export default MemberEditor;
